/**
 * TextSpeedDatingPage.jsx
 * 
 * Canonical Implementation of Daily Text Speed Dating
 * 
 * One-Line Definition:
 * "Daily Text Speed Dating is a continuous series of private, timed, 1:1 text dates that always end cleanly."
 * 
 * Absolute Constraints (Do Not Break):
 * - Never become a group chat
 * - No spectators
 * - No lingering after timer
 * - Never show who passed
 * - No reward for volume over intention
 */

import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import apiClient from "../api/client";
import { Heart, ArrowRight, Clock, Send, Flag, ArrowLeft, RefreshCw, MessageCircle, Info } from "lucide-react";
import { trackEvent } from "../api/analytics";
import { PostEventReflectionModal } from "../components/reflection/PostEventReflectionModal";


// =============================================================================
// ENTRY SCREEN (§1)
// =============================================================================

function EntryScreen({ onStart, status, loading }) {
  const canStart = status?.can_start;
  const remaining = status?.remaining_today || 0;
  const cooldown = status?.cooldown_seconds || 0;
  const activeRoomId = status?.active_room_id;
  const guideRef = React.useRef(null);
  
  // Inline hint state (for "How it works" UX)
  const [howItWorksHint, setHowItWorksHint] = React.useState(false);
  const hintCooldownRef = React.useRef(0);
  const hintTimerRef = React.useRef(null);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (hintTimerRef.current) window.clearTimeout(hintTimerRef.current);
    };
  }, []);

  const scrollToGuide = () => {
    if (!guideRef.current) return;
    
    trackEvent("text_speed_date_how_it_works_clicked");
    guideRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    
    // Highlight ring effect (2s)
    guideRef.current.classList.add("ring-2", "ring-pink-500/50");
    setTimeout(() => {
      guideRef.current?.classList.remove("ring-2", "ring-pink-500/50");
    }, 2000);
    
    // Inline hint (throttled 5s + auto-clear 2.5s)
    const now = Date.now();
    if (now - hintCooldownRef.current < 5000) return;
    hintCooldownRef.current = now;
    
    setHowItWorksHint(true);
    if (hintTimerRef.current) window.clearTimeout(hintTimerRef.current);
    hintTimerRef.current = window.setTimeout(() => {
      setHowItWorksHint(false);
      hintTimerRef.current = null;
    }, 2500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
          <MessageCircle className="w-10 h-10 text-white" />
        </div>

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
          Text Speed Dating
        </h1>
        <p className="text-white/60 text-sm sm:text-base mb-8">
          5 minutes. One person. Real connection.
        </p>

        {/* Status */}
        {status && (
          <div className="mb-6 text-sm text-white/50">
            {remaining > 0 ? (
              <span>{remaining} text dates remaining today</span>
            ) : (
              <span className="text-amber-400">Daily limit reached. Come back tomorrow!</span>
            )}
          </div>
        )}

        {/* Cooldown */}
        {cooldown > 0 && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-300 text-sm">
            <Clock className="inline w-4 h-4 mr-2" />
            Take a breather! Next date available in {cooldown}s
          </div>
        )}

        {/* Active Room */}
        {activeRoomId && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-pink-500/20 border border-pink-500/30 text-pink-300 text-sm">
            You have an active text date!
          </div>
        )}

        {/* Primary CTA */}
        <button
          onClick={onStart}
          disabled={!canStart && !activeRoomId || loading}
          className={`w-full py-4 px-6 rounded-full font-semibold text-lg transition-all ${
            canStart || activeRoomId
              ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700 shadow-lg shadow-pink-500/25"
              : "bg-slate-800 text-slate-500 cursor-not-allowed"
          }`}
          data-testid="start-text-date-btn"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin" />
              Finding someone...
            </span>
          ) : activeRoomId ? (
            "Return to Date"
          ) : (
            "Start Text Date (5 minutes)"
          )}
        </button>

        {/* Secondary Link */}
        <button
          className="mt-4 text-white/50 hover:text-white/70 text-sm flex items-center justify-center gap-1 mx-auto"
          onClick={scrollToGuide}
          data-testid="how-it-works-btn"
        >
          <Info className="w-4 h-4" />
          How it works
        </button>

        {/* How it works (brief) */}
        <div 
          ref={guideRef}
          className="mt-8 text-left bg-slate-900/50 rounded-xl p-4 border border-slate-800 transition-all duration-300"
          data-testid="speed-dating-quick-guide"
        >
          {/* Inline hint - appears on "How it works" tap, throttled + auto-clear */}
          {howItWorksHint && (
            <div
              className="mb-3 inline-flex items-center gap-2 rounded-full border border-pink-400/30 bg-pink-500/10 px-3 py-1 text-sm text-pink-200"
              role="status"
              aria-live="polite"
              data-testid="how-it-works-hint"
            >
              <span aria-hidden="true">👇</span>
              <span>Quick guide below</span>
            </div>
          )}
          <h3 className="text-white/80 font-medium mb-2 text-sm">Quick guide:</h3>
          <ul className="text-white/50 text-xs space-y-1">
            <li>• You&apos;ll be paired with someone for 5 minutes</li>
            <li>• Chat via text only — no pressure</li>
            <li>• When time ends, choose: Interested or Pass</li>
            <li>• Mutual interest = Match (moves to Inbox)</li>
            <li>• No one sees if you passed</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TEXT ROOM (§3)
// =============================================================================

function TextRoom({ room, onSendMessage, onTimeUp, sending }) {
  const { user } = useContext(AuthContext);
  const [message, setMessage] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Calculate seconds remaining
  useEffect(() => {
    if (!room?.ends_at) return;

    const updateTimer = () => {
      const now = new Date();
      const endsAt = new Date(room.ends_at);
      const remaining = Math.max(0, Math.floor((endsAt - now) / 1000));
      setSecondsRemaining(remaining);

      if (remaining === 0) {
        onTimeUp();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [room?.ends_at, onTimeUp]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [room?.messages]);

  // Format timer
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSend = () => {
    if (!message.trim() || sending) return;
    onSendMessage(message.trim());
    setMessage("");
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Timer color based on urgency
  const timerColor = secondsRemaining <= 30 
    ? "text-red-400" 
    : secondsRemaining <= 60 
      ? "text-amber-400" 
      : "text-white/70";

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-black/80">
        <div className="flex items-center gap-3">
          {room?.partner_avatar_url ? (
            <img
              src={room.partner_avatar_url}
              alt=""
              className="w-10 h-10 rounded-full object-cover border-2 border-pink-500/50"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {room?.partner_display_name?.[0] || "?"}
            </div>
          )}
          <div>
            <p className="text-white font-medium text-sm">{room?.partner_display_name || "Your Date"}</p>
            <p className="text-white/50 text-xs">Text Date</p>
          </div>
        </div>

        {/* Timer */}
        <div className={`flex items-center gap-1 px-3 py-1 rounded-full bg-slate-800 ${timerColor}`}>
          <Clock className="w-4 h-4" />
          <span className="font-mono font-bold">{formatTime(secondsRemaining)}</span>
        </div>
      </header>

      {/* Prompt */}
      {room?.prompt && (
        <div className="px-4 py-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-b border-slate-800">
          <p className="text-white/70 text-sm italic text-center">
            💭 {room.prompt}
          </p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {(room?.messages || []).map((msg) => {
          const isMe = msg.sender_id === user?.id;
          return (
            <div
              key={msg.id}
              className={`flex ${isMe ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  isMe
                    ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-br-sm"
                    : "bg-slate-800 text-white rounded-bl-sm"
                }`}
              >
                <p className="text-sm">{msg.text}</p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-slate-800 bg-black/80">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 bg-slate-800 text-white rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/50"
            disabled={sending || secondsRemaining === 0}
            data-testid="text-date-input"
          />
          <button
            onClick={handleSend}
            disabled={!message.trim() || sending || secondsRemaining === 0}
            className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="text-date-send-btn"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// DECISION SCREEN (§5)
// =============================================================================

function DecisionScreen({ room, onDecision, submitting, decision, token }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportNotice, setReportNotice] = useState("");

  async function submitReport() {
    const targetId = room?.partner_id;
    if (!targetId) {
      setReportNotice("Missing partner info. Try refreshing.");
      return;
    }
    const reason = (reportReason || "").trim();
    if (!reason) {
      setReportNotice("Add a short reason so our mods know what happened.");
      return;
    }
    setReportBusy(true);
    setReportNotice("");
    try {
      await apiClient.post(
        "/users/report",
        {
          reported_user_id: targetId,
          reason,
          details: {
            context: "text_speed_dating",
            room_id: room?.id,
            partner_display_name: room?.partner_display_name,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportNotice("Report submitted. Thank you \u2014 we'll review it.");
      setTimeout(() => setReportOpen(false), 900);
      setReportReason("");
    } catch (e) {
      setReportNotice(e?.response?.data?.detail || "Could not submit report.");
    } finally {
      setReportBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-md w-full text-center">
        {/* Partner Info */}
        <div className="mb-6">
          {room?.partner_avatar_url ? (
            <img
              src={room.partner_avatar_url}
              alt=""
              className="w-24 h-24 mx-auto rounded-full object-cover border-4 border-pink-500/50"
            />
          ) : (
            <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold">
              {room?.partner_display_name?.[0] || "?"}
            </div>
          )}
          <h2 className="mt-4 text-xl font-bold text-white">{room?.partner_display_name}</h2>
        </div>

        {/* Time's Up */}
        <div className="mb-8">
          <p className="text-white/60 text-sm">Time's up!</p>
          <p className="text-white text-lg font-medium">How did it go?</p>
        </div>

        {/* Decision Buttons */}
        {!decision ? (
          <div className="flex gap-4 justify-center">
            {/* Pass */}
            <button
              onClick={() => onDecision("pass")}
              disabled={submitting}
              className="flex-1 max-w-[140px] py-4 px-6 rounded-2xl bg-slate-800 text-white/70 hover:bg-slate-700 transition-all flex flex-col items-center gap-2"
              data-testid="text-date-pass-btn"
            >
              <ArrowRight className="w-8 h-8" />
              <span className="font-medium">Pass</span>
            </button>

            {/* Interested */}
            <button
              onClick={() => onDecision("like")}
              disabled={submitting}
              className="flex-1 max-w-[140px] py-4 px-6 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700 transition-all flex flex-col items-center gap-2 shadow-lg shadow-pink-500/25"
              data-testid="text-date-like-btn"
            >
              <Heart className="w-8 h-8" />
              <span className="font-medium">Interested</span>
            </button>
          </div>
        ) : (
          <div className="py-4 px-6 rounded-xl bg-slate-800 text-white/70">
            <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
            <p className="text-sm">Waiting for {room?.partner_display_name}'s choice...</p>
          </div>
        )}

        {/* Report */}
        <button
          className="mt-8 text-white/40 hover:text-white/60 text-xs flex items-center justify-center gap-1 mx-auto"
          onClick={() => {
            setReportNotice("");
            setReportOpen(true);
          }}
        >
          <Flag className="w-3 h-3" />
          Report this interaction
        </button>

        {reportOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/70"
              onClick={() => (reportBusy ? null : setReportOpen(false))}
            />
            <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-slate-950/70 backdrop-blur p-4 sm:p-5 shadow-xl">
              <div className="text-base font-bold text-white">Report</div>
              <div className="mt-1 text-sm text-white/70">
                Tell us what happened with <span className="font-semibold text-white">{room?.partner_display_name || "this user"}</span>.
              </div>

              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                rows={4}
                placeholder="Harassment, spam, fake profile, inappropriate messages\u2026"
                className="mt-4 w-full rounded-2xl border border-white/10 bg-black/30 p-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-pink-400/30"
              />

              {reportNotice ? (
                <div className="mt-3 text-sm text-white/75">{reportNotice}</div>
              ) : null}

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setReportOpen(false)}
                  disabled={reportBusy}
                  className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/80 hover:bg-white/10 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitReport}
                  disabled={reportBusy}
                  className="flex-1 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 px-4 py-3 text-sm font-semibold text-white hover:from-pink-600 hover:to-purple-700 disabled:opacity-50"
                >
                  {reportBusy ? "Sending\u2026" : "Submit"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// =============================================================================
// RESULT SCREEN (§6)
// =============================================================================

function ResultScreen({ result, onStartAnother, onGoToInbox, roomId }) {
  const navigate = useNavigate();
  
  // Post-Event Reflection modal state
  const [showReflection, setShowReflection] = useState(false);
  const hasShownReflectionRef = useRef(false);
  
  // Show reflection modal once on mount
  useEffect(() => {
    if (!hasShownReflectionRef.current) {
      hasShownReflectionRef.current = true;
      // Small delay to let the result screen render first
      const timer = setTimeout(() => setShowReflection(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  if (result.status === "matched") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex flex-col items-center justify-center px-4 py-8">
        <div className="max-w-md w-full text-center">
          {/* Match Animation */}
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center animate-pulse">
            <Heart className="w-12 h-12 text-white" fill="white" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-2">It's a Match! 💕</h1>
          <p className="text-white/60 mb-8">
            You both want to keep talking. Continue in your Inbox!
          </p>

          <button
            onClick={() => navigate(`/inbox`)}
            className="w-full py-4 px-6 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold text-lg hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg shadow-pink-500/25"
            data-testid="go-to-inbox-btn"
          >
            Go to Inbox
          </button>

          <button
            onClick={onStartAnother}
            className="mt-4 text-white/50 hover:text-white/70 text-sm"
          >
            Start another text date
          </button>
        </div>
        
        {/* Post-Event Reflection Modal */}
        <PostEventReflectionModal
          open={showReflection}
          onClose={() => setShowReflection(false)}
          eventType="text_speed_dating"
          eventId={roomId}
        />
      </div>
    );
  }

  // Passed or no match
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-slate-950 to-black flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-800 flex items-center justify-center">
          <MessageCircle className="w-10 h-10 text-white/50" />
        </div>

        <h1 className="text-xl font-bold text-white mb-2">Thanks for being present</h1>
        <p className="text-white/60 mb-8">
          Ready for another connection?
        </p>

        <button
          onClick={onStartAnother}
          className="w-full py-4 px-6 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold text-lg hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg shadow-pink-500/25"
          data-testid="start-another-btn"
        >
          Start Another Text Date
        </button>

        <button
          onClick={() => navigate("/dashboard")}
          className="mt-4 text-white/50 hover:text-white/70 text-sm"
        >
          Take a break
        </button>
      </div>
      
      {/* Post-Event Reflection Modal */}
      <PostEventReflectionModal
        open={showReflection}
        onClose={() => setShowReflection(false)}
        eventType="text_speed_dating"
        eventId={roomId}
      />
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function TextSpeedDatingPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  // State
  const [screen, setScreen] = useState("entry"); // entry, room, decision, result
  const [status, setStatus] = useState(null);
  const [room, setRoom] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [decision, setDecision] = useState(null);
  const [error, setError] = useState(null);

  // Polling interval for room updates
  const pollInterval = useRef(null);

  // Fetch user status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get(`/speed-dating/text/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
      setError(null); // Clear any previous errors on success

      // If there's an active room, load it
      if (res.data.active_room_id) {
        await loadRoom(res.data.active_room_id);
      }
    } catch (err) {
      console.error("[TextSpeedDating] Failed to fetch status:", err);
      // Show a user-friendly message instead of leaving the page blank
      if (!err.response) {
        setError("Unable to reach the server. Check your connection and try again.");
      } else {
        setError(err.response?.data?.detail || "Failed to load status. Please refresh.");
      }
    }
  }, [token]);

  // Load room data
  const loadRoom = useCallback(async (roomId) => {
    try {
      const res = await apiClient.get(`/speed-dating/text/room/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoom(res.data);

      if (res.data.status === "active") {
        setScreen("room");
      } else if (res.data.status === "decision_pending") {
        setScreen("decision");
      }
    } catch (err) {
      console.error("[TextSpeedDating] Failed to load room:", err);
    }
  }, [token]);

  // Start polling when in room
  useEffect(() => {
    if (screen === "room" && room?.room_id) {
      pollInterval.current = setInterval(async () => {
        try {
          const res = await apiClient.get(`/speed-dating/text/room/${room.room_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setRoom(res.data);

          if (res.data.status === "decision_pending") {
            setScreen("decision");
          }
        } catch (err) {
          console.error("[TextSpeedDating] Poll error:", err);
        }
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(pollInterval.current);
    }
  }, [screen, room?.room_id, token]);

  // Initial load
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Start a text date
  const handleStart = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await apiClient.post(
        `/speed-dating/text/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "no_partner") {
        // No partner available
        setError(res.data.message);
        setTimeout(() => setError(null), 5000);
      } else {
        // Got a room!
        setRoom({
          room_id: res.data.room_id,
          partner_id: res.data.partner_id,
          partner_display_name: res.data.partner_display_name,
          partner_avatar_url: res.data.partner_avatar_url,
          prompt: res.data.prompt,
          ends_at: res.data.ends_at,
          messages: [],
        });
        setScreen("room");

        trackEvent("text_speed_date_started", {
          room_id: res.data.room_id,
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!err.response) {
        setError("Unable to reach the server. Check your connection and try again.");
      } else if (typeof detail === "object") {
        setError(detail.message || "Something went wrong");
      } else {
        setError(detail || "Failed to start text date. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Send a message
  const handleSendMessage = async (text) => {
    if (!room?.room_id) return;
    setSending(true);

    try {
      const res = await apiClient.post(
        `/speed-dating/text/room/${room.room_id}/message`,
        { room_id: room.room_id, text },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add message to local state
      setRoom((prev) => ({
        ...prev,
        messages: [...(prev.messages || []), res.data.message],
      }));
    } catch (err) {
      console.error("[TextSpeedDating] Failed to send message:", err);
      // Could show error toast
    } finally {
      setSending(false);
    }
  };

  // Timer ended
  const handleTimeUp = () => {
    setScreen("decision");
  };

  // Submit decision
  const handleDecision = async (choice) => {
    if (!room?.room_id) return;
    setDecision(choice);

    try {
      const res = await apiClient.post(
        `/speed-dating/text/room/${room.room_id}/decision`,
        { room_id: room.room_id, decision: choice },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "matched" || res.data.status === "passed") {
        setResult(res.data);
        setScreen("result");
      }
      // If "waiting", stay on decision screen
    } catch (err) {
      console.error("[TextSpeedDating] Failed to submit decision:", err);
      setDecision(null);
    }
  };

  // Start another
  const handleStartAnother = () => {
    setRoom(null);
    setResult(null);
    setDecision(null);
    setScreen("entry");
    fetchStatus();
  };

  // FIX #4: State for leave confirmation modal
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Handle back button with mid-round confirmation
  const handleBack = () => {
    if (screen === "room") {
      // Mid-round: show confirmation
      setShowLeaveConfirm(true);
    } else {
      // Not in round: navigate back to hub
      navigate("/speed-dating");
    }
  };

  // Confirm leave during round
  const confirmLeave = () => {
    setShowLeaveConfirm(false);
    setRoom(null);
    setScreen("entry");
    navigate("/speed-dating");
  };

  // Render based on screen
  return (
    <div className="relative">
      {/* FIX #4: Back button - visible on all screens */}
      <button
        onClick={handleBack}
        className="absolute top-4 left-4 z-10 text-white/50 hover:text-white/70 flex items-center gap-1 transition-colors"
        data-testid="text-speed-dating-back-btn"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="text-sm">Back</span>
      </button>

      {/* FIX #4: Leave confirmation modal for mid-round exit */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full text-center">
            <h3 className="text-xl font-bold text-white mb-2">Leave this round?</h3>
            <p className="text-white/60 text-sm mb-6">
              You can start another round anytime.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 py-3 rounded-xl bg-slate-800 text-white/70 hover:bg-slate-700 transition-colors"
              >
                Stay
              </button>
              <button
                onClick={confirmLeave}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors"
                data-testid="confirm-leave-btn"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error toast */}
      {error && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-4 py-2 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-300 text-sm">
          {error}
        </div>
      )}

      {/* Screens */}
      {screen === "entry" && (
        <EntryScreen
          onStart={handleStart}
          status={status}
          loading={loading}
        />
      )}

      {screen === "room" && (
        <TextRoom
          room={room}
          onSendMessage={handleSendMessage}
          onTimeUp={handleTimeUp}
          sending={sending}
        />
      )}

      {screen === "decision" && (
        <DecisionScreen
          room={room}
          onDecision={handleDecision}
          submitting={!!decision}
          decision={decision}
          token={token}
        />
      )}

      {screen === "result" && (
        <ResultScreen
          result={result}
          onStartAnother={handleStartAnother}
          roomId={room?.room_id}
        />
      )}
    </div>
  );
}
